import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Globe, BookOpen, Calendar, FileText, AlertTriangle,
  ChevronDown, ChevronRight, Clock, Building2, Scale,
  ExternalLink, ArrowRight, Filter, X, MapPin, CheckCircle2,
  Search, ArrowUpDown, SlidersHorizontal, Map, Film
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import InteractiveWorldMap from '@/components/insights/InteractiveWorldMap';

/* ─── Detailed mandate data for countries with full briefs ─── */
const DETAILED_MANDATES = {
  RO: {
    id: 'romania-saft',
    overview: 'Romania has implemented the SAF-T (Standard Audit File for Tax) reporting requirement as part of its broader fiscal digitalization strategy. The Romanian SAF-T, known locally as D406, is based on the OECD SAF-T v2.0 schema and requires taxpayers to submit detailed transactional data to ANAF in a standardized XML format.',
    keyDates: [
      { date: 'January 1, 2022', event: 'Mandatory for large taxpayers (first wave)' },
      { date: 'January 1, 2023', event: 'Extended to medium-sized taxpayers' },
      { date: 'January 1, 2025', event: 'Extended to small taxpayers and non-resident VAT registrants' },
    ],
    requirements: [
      'Monthly submission of D406 declaration to ANAF',
      'XML format based on OECD SAF-T v2.0 schema with Romanian-specific extensions',
      'Submission deadline: by the end of the month following the reporting period',
      'Penalties for late or incorrect filing: 1,000 - 5,000 RON per occurrence',
    ],
    authority: { name: 'ANAF - National Agency for Fiscal Administration', url: 'https://www.anaf.ro' },
    taxeraCapability: 'Taxera fully supports Romanian SAF-T D406 generation, validation, and submission. Our middleware extracts data directly from your ERP, maps it to the ANAF schema, and handles updates automatically.',
    relatedMandates: ['Romania e-Invoicing (RO e-Factura)', 'Romania eTransport'],
  },
  IT: {
    id: 'italy-einvoicing',
    overview: 'Italy was the first EU country to mandate B2B electronic invoicing, effective January 1, 2019. All invoices must be transmitted through the SDI (Sistema di Interscambio), the government-operated clearance platform. Italy\'s model has become the reference for other EU countries designing CTC systems.',
    keyDates: [
      { date: 'January 1, 2019', event: 'B2B and B2C e-Invoicing mandatory for all VAT-registered businesses' },
      { date: 'January 1, 2024', event: 'Extended to all flat-rate taxpayers regardless of revenue' },
      { date: '2026', event: 'Cross-border e-Invoicing via PEPPOL integration planned' },
    ],
    requirements: [
      'All domestic B2B and B2C invoices must pass through SDI',
      'Invoice format: FatturaPA XML (version 1.2.2)',
      'Electronic storage (conservazione sostitutiva) mandatory for 10 years',
      'Penalties: 5% to 10% of undeclared transaction value',
    ],
    authority: { name: 'Agenzia delle Entrate', url: 'https://www.agenziaentrate.gov.it' },
    taxeraCapability: 'Taxera supports the full Italian SDI workflow: FatturaPA generation, real-time SDI submission, status tracking, and compliant long-term storage. Cross-border via PEPPOL is supported.',
    relatedMandates: ['Italy SdI Cross-border (PEPPOL)', 'Italy VAT Pre-filled Returns'],
  },
  FR: {
    id: 'france-einvoicing',
    overview: 'France is implementing a dual obligation: e-Invoicing (domestic B2B) and eReporting (B2C and cross-border). The system uses a Y-model where invoices are exchanged via certified Partner Dematerialization Platforms (PDPs) and simultaneously reported to the public portal Chorus Pro / PPF.',
    keyDates: [
      { date: 'September 1, 2026', event: 'Large enterprises must issue and receive e-Invoices + eReporting' },
      { date: 'September 1, 2027', event: 'Mid-sized and small enterprises must issue and receive e-Invoices' },
    ],
    requirements: [
      'B2B domestic invoices must be issued electronically via a registered PDP or PPF',
      'eReporting required for B2C and cross-border sales',
      'Supported formats: Factur-X, UBL, and CII',
      'Penalties: 15 EUR per invoice for non-compliance, 250 EUR per report for eReporting failures',
    ],
    authority: { name: 'DGFIP - Direction Generale des Finances Publiques', url: 'https://www.impots.gouv.fr' },
    taxeraCapability: 'Taxera supports the French e-Invoicing and eReporting mandates through PDP-ready infrastructure. Our platform generates Factur-X, UBL, and CII formats, handles PDP routing and eReporting submissions.',
    relatedMandates: ['France eReporting (B2C & Cross-border)', 'EU ViDA Initiative'],
  },
  DE: {
    id: 'germany-einvoicing',
    overview: 'Germany has legislated mandatory B2B e-Invoicing as part of the Growth Opportunities Act (Wachstumschancengesetz). Starting January 2025, all businesses must receive structured electronic invoices. Issuance is phased in, with full mandatory issuance from January 2028.',
    keyDates: [
      { date: 'January 1, 2025', event: 'All businesses must be able to receive e-Invoices' },
      { date: 'January 1, 2027', event: 'Mandatory issuance for businesses with revenue > 800K EUR' },
      { date: 'January 1, 2028', event: 'Mandatory issuance for all businesses' },
    ],
    requirements: [
      'e-Invoices must conform to EN 16931 European standard',
      'Accepted formats: XRechnung (XML) and ZUGFeRD/Factur-X',
      'PDF-only invoices will no longer be valid for B2B',
      'Archiving obligations under GoBD apply',
    ],
    authority: { name: 'Bundesministerium der Finanzen (BMF)', url: 'https://www.bundesfinanzministerium.de' },
    taxeraCapability: 'Taxera generates XRechnung and ZUGFeRD/Factur-X natively from your ERP. Our middleware handles peer-to-peer delivery, format validation, and GoBD-compliant archiving.',
    relatedMandates: ['Germany VAT Returns', 'EU ViDA Initiative'],
  },
  PL: {
    id: 'poland-ksef',
    overview: 'Poland\'s Krajowy System e-Faktur (KSeF) is a government-operated National e-Invoicing System serving as the central clearance platform for all B2B invoices. KSeF became mandatory for large taxpayers in February 2026.',
    keyDates: [
      { date: 'January 1, 2022', event: 'KSeF launched as voluntary system' },
      { date: 'February 1, 2026', event: 'Mandatory for large taxpayers (revenue > PLN 200M)' },
      { date: 'April 1, 2026', event: 'Mandatory for all remaining VAT-registered taxpayers' },
    ],
    requirements: [
      'All B2B invoices must be issued via KSeF',
      'Invoice format: FA(2) XML schema',
      'Real-time validation by KSeF before delivery to buyer',
      'Penalties for issuing outside KSeF: up to 100% of the VAT amount',
    ],
    authority: { name: 'Ministerstwo Finansow', url: 'https://www.gov.pl/web/finanse' },
    taxeraCapability: 'Taxera provides full KSeF integration: FA(2) XML generation, API-based submission, real-time status tracking, reference number management, and compliant archiving.',
    relatedMandates: ['Poland SAF-T (JPK)', 'Poland VAT Returns'],
  },
};

/* ─── Hierarchical region -> country -> mandate structure ─── */
const REGIONS = [
  {
    id: 'europe',
    name: 'Europe',
    accentColor: '#0066CC',
    countries: [
      { code: 'RO', name: 'Romania', mandates: [{ type: 'SAF-T', category: 'SAF-T', status: 'live', statusLabel: 'In Force' }] },
      { code: 'IT', name: 'Italy', mandates: [{ type: 'e-Invoicing (SDI)', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'FR', name: 'France', mandates: [{ type: 'e-Invoicing & eReporting', category: 'e-Invoicing', status: 'rollout', statusLabel: 'Rollout 2026' }] },
      { code: 'DE', name: 'Germany', mandates: [{ type: 'B2B e-Invoicing', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'Mandatory 2027' }] },
      { code: 'PL', name: 'Poland', mandates: [{ type: 'KSeF e-Invoicing', category: 'e-Invoicing', status: 'live', statusLabel: 'Mandatory Feb 2026' }] },
      { code: 'ES', name: 'Spain', mandates: [{ type: 'Veri*factu / SII', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'PT', name: 'Portugal', mandates: [{ type: 'SAF-T', category: 'SAF-T', status: 'live', statusLabel: 'In Force' }] },
      { code: 'BE', name: 'Belgium', mandates: [{ type: 'B2B e-Invoicing', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'Mandatory 2026' }] },
      { code: 'TR', name: 'Turkey', mandates: [{ type: 'eInvoice / eArchive', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'NO', name: 'Norway', mandates: [{ type: 'SAF-T Financial', category: 'SAF-T', status: 'live', statusLabel: 'In Force' }] },
      { code: 'HU', name: 'Hungary', mandates: [{ type: 'Real-Time Invoice Reporting', category: 'Real-Time Reporting', status: 'live', statusLabel: 'In Force' }] },
      { code: 'GR', name: 'Greece', mandates: [{ type: 'myDATA e-Invoicing', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'RS', name: 'Serbia', mandates: [{ type: 'SEF e-Invoicing', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'HR', name: 'Croatia', mandates: [{ type: 'Fiscalization', category: 'Real-Time Reporting', status: 'live', statusLabel: 'In Force' }] },
      { code: 'LT', name: 'Lithuania', mandates: [{ type: 'i.SAF / i.VAZ', category: 'SAF-T', status: 'live', statusLabel: 'In Force' }] },
      { code: 'CZ', name: 'Czech Republic', mandates: [{ type: 'e-Invoicing (planned)', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'Planned 2026' }] },
      { code: 'SK', name: 'Slovakia', mandates: [{ type: 'e-Invoicing (planned)', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'Planned 2027' }] },
      { code: 'UA', name: 'Ukraine', mandates: [{ type: 'e-Invoicing (planned)', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'In Development' }] },
      { code: 'GB', name: 'United Kingdom', mandates: [{ type: 'MTD VAT / MTD ITSA', category: 'Digital Tax', status: 'live', statusLabel: 'MTD Active' }] },
      { code: 'SE', name: 'Sweden', mandates: [{ type: 'PEPPOL / SAF-T (planned)', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'Monitoring' }] },
    ],
  },
  {
    id: 'middle-east',
    name: 'Middle East',
    accentColor: '#f59e0b',
    countries: [
      { code: 'SA', name: 'Saudi Arabia', mandates: [{ type: 'ZATCA e-Invoicing', category: 'e-Invoicing', status: 'live', statusLabel: 'Phase 2 Active' }] },
      { code: 'AE', name: 'UAE', mandates: [{ type: 'e-Invoicing (planned)', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'In Development' }] },
      { code: 'JO', name: 'Jordan', mandates: [{ type: 'JOFOTARA e-Invoicing', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'OM', name: 'Oman', mandates: [{ type: 'e-Invoicing (planned)', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'Planned' }] },
    ],
  },
  {
    id: 'asia-pacific',
    name: 'Asia-Pacific',
    accentColor: '#8b5cf6',
    countries: [
      { code: 'IN', name: 'India', mandates: [{ type: 'e-Invoicing (IRP)', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'CN', name: 'China', mandates: [{ type: 'Golden Tax (Fapiao)', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'JP', name: 'Japan', mandates: [{ type: 'Qualified Invoice System', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'KR', name: 'South Korea', mandates: [{ type: 'NTS e-Invoicing', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'ID', name: 'Indonesia', mandates: [{ type: 'e-Faktur / Coretax', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'MY', name: 'Malaysia', mandates: [{ type: 'MyInvois e-Invoicing', category: 'e-Invoicing', status: 'live', statusLabel: 'Mandatory 2024' }] },
      { code: 'PH', name: 'Philippines', mandates: [{ type: 'EIS e-Invoicing', category: 'e-Invoicing', status: 'live', statusLabel: 'Phased Rollout' }] },
      { code: 'TH', name: 'Thailand', mandates: [{ type: 'eTax Invoice', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'VN', name: 'Vietnam', mandates: [{ type: 'e-Invoicing', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'TW', name: 'Taiwan', mandates: [{ type: 'eGUI Uniform Invoice', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'AU', name: 'Australia', mandates: [{ type: 'PEPPOL e-Invoicing / STP', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'Expanding' }] },
      { code: 'NZ', name: 'New Zealand', mandates: [{ type: 'PEPPOL e-Invoicing', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'Gov. Push' }] },
      { code: 'SG', name: 'Singapore', mandates: [{ type: 'InvoiceNow (PEPPOL)', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'Voluntary' }] },
    ],
  },
  {
    id: 'americas',
    name: 'Americas',
    accentColor: '#ef4444',
    countries: [
      { code: 'US', name: 'United States', mandates: [{ type: '1099 eFiling / State Sales Tax', category: 'Digital Tax', status: 'live', statusLabel: 'State-Level' }] },
      { code: 'CA', name: 'Canada', mandates: [{ type: 'GST/HST / e-Invoicing (pilot)', category: 'Digital Tax', status: 'upcoming', statusLabel: 'Monitoring' }] },
      { code: 'MX', name: 'Mexico', mandates: [{ type: 'CFDI 4.0', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'CO', name: 'Colombia', mandates: [{ type: 'Electronic Invoice', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'BR', name: 'Brazil', mandates: [{ type: 'NF-e / NFS-e', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'AR', name: 'Argentina', mandates: [{ type: 'Factura Electronica (AFIP)', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'CL', name: 'Chile', mandates: [{ type: 'DTE (SII)', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'PE', name: 'Peru', mandates: [{ type: 'CPE (SUNAT)', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'EC', name: 'Ecuador', mandates: [{ type: 'e-Invoicing (SRI)', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'UY', name: 'Uruguay', mandates: [{ type: 'CFE (DGI)', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'CR', name: 'Costa Rica', mandates: [{ type: 'e-Invoicing (Hacienda)', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
    ],
  },
  {
    id: 'africa',
    name: 'Africa',
    accentColor: '#059669',
    countries: [
      { code: 'NG', name: 'Nigeria', mandates: [{ type: 'e-Invoicing / TIN', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'KE', name: 'Kenya', mandates: [{ type: 'eTIMS', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'EG', name: 'Egypt', mandates: [{ type: 'e-Invoicing / eReceipt', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'GH', name: 'Ghana', mandates: [{ type: 'eVAT / E-Levy', category: 'e-Invoicing', status: 'live', statusLabel: 'In Force' }] },
      { code: 'TZ', name: 'Tanzania', mandates: [{ type: 'EFD (Fiscal Devices)', category: 'Fiscal Device', status: 'live', statusLabel: 'EFD Mandatory' }] },
      { code: 'RW', name: 'Rwanda', mandates: [{ type: 'EBM (Billing Machines)', category: 'Fiscal Device', status: 'live', statusLabel: 'EBM Mandatory' }] },
      { code: 'UG', name: 'Uganda', mandates: [{ type: 'EFRIS', category: 'e-Invoicing', status: 'live', statusLabel: 'EFRIS Active' }] },
      { code: 'ZA', name: 'South Africa', mandates: [{ type: 'e-Invoicing (pilot)', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'Pilot Phase' }] },
      { code: 'MA', name: 'Morocco', mandates: [{ type: 'e-Invoicing (planned)', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'In Development' }] },
      { code: 'ET', name: 'Ethiopia', mandates: [{ type: 'eReceipt (planned)', category: 'e-Invoicing', status: 'upcoming', statusLabel: 'In Development' }] },
    ],
  },
];

const STATUS_COLORS = { live: '#10b981', rollout: '#f59e0b', upcoming: '#3b82f6' };
const STATUS_ORDER = { upcoming: 0, rollout: 1, live: 2 };

/* ─── Country code → flag image URL (works on all platforms) ─── */
const flagUrl = (code) =>
  `https://flagcdn.com/w80/${code.toLowerCase()}.png`;

const SORT_OPTIONS = [
  { value: 'name', label: 'A - Z' },
  { value: 'name-desc', label: 'Z - A' },
  { value: 'status', label: 'By status' },
];

/* ─── Multi-select toggle helper ─── */
const toggleSet = (set, val) => {
  const next = new Set(set);
  if (next.has(val)) next.delete(val);
  else next.add(val);
  return next;
};

/* ─── Component ─── */
const InsightsPage = () => {
  const [activeRegions, setActiveRegions] = useState(new Set(['europe']));
  const [mandateFilters, setMandateFilters] = useState(new Set());
  const [expandedCountry, setExpandedCountry] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const hasActiveFilters = mandateFilters.size > 0 || countrySearch;

  // Merge countries from all selected regions
  const allCountries = useMemo(() => {
    const merged = [];
    activeRegions.forEach(regionId => {
      const region = REGIONS.find(r => r.id === regionId);
      if (region) {
        region.countries.forEach(c => {
          if (!merged.find(m => m.code === c.code)) {
            merged.push({ ...c, regionId: region.id, regionName: region.name, accentColor: region.accentColor });
          }
        });
      }
    });
    return merged;
  }, [activeRegions]);

  // Available mandate categories from selected regions
  const availableCategories = useMemo(() => {
    const cats = new Set();
    allCountries.forEach(c => c.mandates.forEach(m => cats.add(m.category)));
    return [...cats].sort();
  }, [allCountries]);

  // Filtered + searched + sorted countries
  const filteredCountries = useMemo(() => {
    let result = allCountries;
    if (mandateFilters.size > 0) {
      result = result.filter(c => c.mandates.some(m => mandateFilters.has(m.category)));
    }
    if (countrySearch.trim()) {
      const q = countrySearch.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.mandates.some(m => m.type.toLowerCase().includes(q))
      );
    }
    // Sort
    result = [...result].sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'name-desc') return b.name.localeCompare(a.name);
      if (sortBy === 'status') return (STATUS_ORDER[a.mandates[0]?.status] ?? 3) - (STATUS_ORDER[b.mandates[0]?.status] ?? 3);
      return 0;
    });
    return result;
  }, [allCountries, mandateFilters, countrySearch, sortBy]);

  const toggleRegion = (id) => {
    const next = toggleSet(activeRegions, id);
    if (next.size === 0) next.add(id); // always at least one region
    setActiveRegions(next);
    setExpandedCountry(null);
  };

  const toggleMandate = (cat) => {
    setMandateFilters(toggleSet(mandateFilters, cat));
    setExpandedCountry(null);
  };

  const clearFilters = () => {
    setMandateFilters(new Set());
    setCountrySearch('');
    setSortBy('name');
    setExpandedCountry(null);
  };

  const handleCountryClick = (code) => {
    setExpandedCountry(prev => prev === code ? null : code);
  };

  const expandedDetail = expandedCountry ? DETAILED_MANDATES[expandedCountry] : null;
  const expandedCountryData = expandedCountry ? filteredCountries.find(c => c.code === expandedCountry) : null;

  return (
    <PageLayout>
      {/* Hero */}
      <section className="pb-16 pt-8" data-testid="insights-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-taxera-teal/10 text-taxera-teal text-sm font-medium rounded-full mb-6">
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Regulatory Intelligence
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-tight mb-5">
              The world is <br />
              <span className="taxera-gradient-text">changing fast.</span>
            </h1>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Governments are digitalizing tax compliance at an unprecedented pace. New mandates, new formats, new deadlines. Stay ahead with Taxera's regulatory intelligence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="pb-16" data-testid="insights-stats">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { value: '135+', label: 'Countries with tax digitalization', icon: Globe },
              { value: '2026', label: 'France & Belgium go mandatory', icon: Calendar },
              { value: '55+', label: 'Countries tracked on this map', icon: MapPin },
              { value: 'Every quarter', label: 'New mandates or changes emerge', icon: AlertTriangle },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-2xl border border-slate-200 p-6 text-center hover:shadow-md transition-shadow duration-300"
                  data-testid={`insight-stat-${i}`}
                >
                  <Icon className="w-5 h-5 text-taxera-teal mx-auto mb-3" strokeWidth={1.5} />
                  <div className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal">{stat.value}</div>
                  <div className="text-xs text-slate-500 mt-1 font-medium">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Interactive World Map ─── */}
      <section className="pb-16" data-testid="insights-world-map">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Map className="w-5 h-5 text-taxera-blue" strokeWidth={1.5} />
                  <h2 className="font-heading text-lg font-bold text-taxera-charcoal">Global Compliance Map</h2>
                </div>
                <p className="text-sm text-slate-500">Click any highlighted country to view mandate details and filter the knowledge base below.</p>
              </div>
            </div>
            <InteractiveWorldMap onCountrySelect={(name) => {
              setCountrySearch(name);
              const el = document.getElementById('mandate-explorer');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }} />
          </motion.div>
        </div>
      </section>

      {/* ─── Mandate Explorer ─── */}
      <section className="pb-24" id="mandate-explorer" data-testid="mandate-explorer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-10">
            <h2 className="font-heading text-lg font-bold text-taxera-charcoal mb-2">
              Explore by region
            </h2>
            <p className="text-sm text-slate-500">
              Select regions, filter by mandate type, search countries. Multi-select supported.
            </p>
          </div>

          {/* Region selector — multi-select */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2" data-testid="region-selector">
              <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide mr-1 shrink-0">Region:</span>
              {REGIONS.map(r => (
                <button
                  key={r.id}
                  onClick={() => toggleRegion(r.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                    activeRegions.has(r.id)
                      ? 'text-white shadow-sm'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  style={activeRegions.has(r.id) ? { backgroundColor: r.accentColor } : {}}
                  data-testid={`region-tab-${r.id}`}
                >
                  {r.name}
                  <span className="ml-1.5 opacity-70">({r.countries.length})</span>
                </button>
              ))}
            </div>

            {/* Mandate type filter — multi-select */}
            {availableCategories.length > 0 && (
              <div className="flex flex-wrap items-center gap-2" data-testid="mandate-type-filter">
                <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide mr-1 shrink-0">Mandate:</span>
                {availableCategories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => toggleMandate(cat)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                      mandateFilters.has(cat)
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`mandate-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Search + Sort row */}
            <div className="flex items-center gap-3 pt-1">
              <div className="relative w-full max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={countrySearch}
                  onChange={(e) => { setCountrySearch(e.target.value); setExpandedCountry(null); }}
                  placeholder="Search countries or mandates..."
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20"
                  data-testid="insights-country-search"
                />
              </div>

              {/* Sort dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:border-slate-300 transition-colors bg-white whitespace-nowrap"
                  data-testid="insights-sort-btn"
                >
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  {SORT_OPTIONS.find(s => s.value === sortBy)?.label}
                  <ChevronDown className={`w-3 h-3 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showSortDropdown && (
                  <div className="absolute top-full right-0 mt-1 w-36 bg-white rounded-xl border border-slate-200 shadow-xl z-30 py-1">
                    {SORT_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => { setSortBy(opt.value); setShowSortDropdown(false); }}
                        className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                          sortBy === opt.value ? 'bg-taxera-blue/5 text-taxera-blue font-bold' : 'text-slate-600 hover:bg-slate-50'
                        }`}
                        data-testid={`insights-sort-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">
                {filteredCountries.length} of {allCountries.length} countries
              </span>
            </div>

            {/* Active filters bar */}
            {hasActiveFilters && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap items-center gap-2 pt-1"
                data-testid="insights-active-filters"
              >
                <SlidersHorizontal className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                {[...mandateFilters].map(m => (
                  <span
                    key={m}
                    className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-slate-100 text-slate-700 text-xs font-semibold rounded-full"
                    data-testid={`active-mandate-${m.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {m}
                    <button
                      onClick={() => toggleMandate(m)}
                      className="w-4 h-4 rounded-full hover:bg-slate-300/50 flex items-center justify-center transition-colors"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
                {countrySearch && (
                  <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-slate-100 text-slate-700 text-xs font-semibold rounded-full">
                    "{countrySearch}"
                    <button
                      onClick={() => setCountrySearch('')}
                      className="w-4 h-4 rounded-full hover:bg-slate-300/50 flex items-center justify-center transition-colors"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="px-3 py-1 rounded-full text-xs font-bold text-red-500 hover:bg-red-50 transition-colors"
                  data-testid="insights-clear-filters"
                >
                  Clear all
                </button>
              </motion.div>
            )}
          </div>

          {/* Country grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-8" data-testid="country-grid">
            {filteredCountries.map((country, i) => {
              const hasDetail = !!DETAILED_MANDATES[country.code];
              const mandate = country.mandates[0];
              const isExpanded = expandedCountry === country.code;
              const statusColor = STATUS_COLORS[mandate.status] || '#94a3b8';

              return (
                <motion.button
                  key={country.code}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  onClick={() => hasDetail ? handleCountryClick(country.code) : null}
                  className={`text-left rounded-2xl border p-5 transition-colors duration-200 ${
                    isExpanded
                      ? 'bg-slate-900 border-slate-700 ring-2 ring-taxera-teal/30'
                      : hasDetail
                        ? 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md cursor-pointer'
                        : 'bg-slate-50 border-slate-100 cursor-default'
                  }`}
                  data-testid={`country-card-${country.code.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <img
                      src={flagUrl(country.code)}
                      alt={country.name}
                      className="w-10 h-7 object-cover rounded shadow-sm"
                    />
                    <span
                      className="text-2xs px-2 py-0.5 rounded-full font-bold"
                      style={{ backgroundColor: `${statusColor}18`, color: statusColor }}
                    >
                      {mandate.statusLabel}
                    </span>
                  </div>
                  <h3 className={`font-heading text-sm font-bold mb-1 ${isExpanded ? 'text-white' : 'text-taxera-charcoal'}`}>
                    {country.name}
                  </h3>
                  <p className={`text-xs mb-2 ${isExpanded ? 'text-slate-400' : 'text-slate-500'}`}>
                    {mandate.type}
                  </p>
                  {activeRegions.size > 1 && (
                    <span className="text-2xs font-semibold text-slate-400 uppercase tracking-wide">{country.regionName}</span>
                  )}
                  {hasDetail ? (
                    <span className={`inline-flex items-center gap-1 text-2xs font-semibold mt-1 ${
                      isExpanded ? 'text-taxera-teal' : 'text-taxera-blue'
                    }`}>
                      {isExpanded ? 'Viewing brief' : 'View brief'} <ChevronRight className="w-3 h-3" />
                    </span>
                  ) : (
                    <span className="text-2xs text-slate-400 font-medium block mt-1">Brief coming soon</span>
                  )}
                </motion.button>
              );
            })}
          </div>

          {filteredCountries.length === 0 && (
            <div className="text-center py-16 text-slate-400" data-testid="no-countries">
              <MapPin className="w-8 h-8 mx-auto mb-3" />
              <p className="text-sm mb-3">No countries match your filters.</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 rounded-xl bg-taxera-blue text-white text-xs font-semibold hover:bg-taxera-blue/90 transition-colors"
                  data-testid="insights-empty-clear-btn"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {/* Expanded mandate detail panel */}
          <AnimatePresence>
            {expandedDetail && expandedCountryData && (
              <motion.div
                key={`detail-${expandedCountry}`}
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="bg-white rounded-2xl border border-slate-200 shadow-lg" data-testid={`mandate-detail-${expandedCountry?.toLowerCase()}`}>
                  <div className="flex items-center justify-between p-6 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <img
                        src={flagUrl(expandedCountryData.code)}
                        alt={expandedCountryData.name}
                        className="w-12 h-8 object-cover rounded shadow-sm"
                      />
                      <div>
                        <h3 className="font-heading text-lg font-bold text-taxera-charcoal">{expandedCountryData.name}</h3>
                        <p className="text-sm text-slate-500">{expandedCountryData.mandates[0].type}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setExpandedCountry(null)}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                      data-testid="close-detail"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="p-6 md:p-8">
                    <div className="grid lg:grid-cols-3 gap-8">
                      <div className="lg:col-span-2 space-y-6">
                        <div>
                          <div className="text-2xs font-bold tracking-[0.15em] text-slate-400 uppercase mb-3">Overview</div>
                          <p className="text-sm text-slate-700 leading-relaxed">{expandedDetail.overview}</p>
                        </div>
                        <div>
                          <div className="text-2xs font-bold tracking-[0.15em] text-slate-400 uppercase mb-3">Key dates</div>
                          <div className="space-y-2.5">
                            {expandedDetail.keyDates.map((kd, idx) => (
                              <div key={`kd-${kd.date}-${kd.event.slice(0, 20)}`} className="flex items-start gap-3">
                                <div className="shrink-0 w-1.5 h-1.5 rounded-full bg-taxera-teal mt-1.5" />
                                <div>
                                  <span className="text-sm font-semibold text-taxera-charcoal">{kd.date}</span>
                                  <span className="text-xs text-slate-500 ml-2">{kd.event}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-2xs font-bold tracking-[0.15em] text-slate-400 uppercase mb-3">Requirements</div>
                          <ul className="space-y-2">
                            {expandedDetail.requirements.map((req, idx) => (
                              <li key={`req-${req.slice(0, 30)}`} className="flex items-start gap-2">
                                <Scale className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                                <span className="text-sm text-slate-700 leading-relaxed">{req}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                          <div className="text-2xs font-bold tracking-[0.15em] text-slate-400 uppercase mb-2">Tax authority</div>
                          <div className="flex items-start gap-2">
                            <Building2 className="w-4 h-4 text-taxera-blue shrink-0 mt-0.5" strokeWidth={1.5} />
                            <div>
                              <div className="text-sm font-semibold text-taxera-charcoal">{expandedDetail.authority.name}</div>
                              <a href={expandedDetail.authority.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-taxera-blue hover:underline mt-1" data-testid="authority-link">
                                Visit website <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </div>
                        </div>
                        <div className="bg-slate-900 rounded-xl p-4">
                          <div className="text-2xs font-bold tracking-[0.15em] text-taxera-teal uppercase mb-2">Taxera coverage</div>
                          <p className="text-sm text-slate-300 leading-relaxed">{expandedDetail.taxeraCapability}</p>
                          <div className="mt-3 flex items-center gap-1 text-xs text-taxera-teal font-semibold">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Fully supported
                          </div>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                          <div className="text-2xs font-bold tracking-[0.15em] text-slate-400 uppercase mb-2">Related mandates</div>
                          <div className="space-y-1.5">
                            {expandedDetail.relatedMandates.map((rm, idx) => (
                              <div key={`rm-${rm.slice(0, 30)}`} className="flex items-center gap-2 text-sm text-slate-600">
                                <ChevronRight className="w-3 h-3 text-slate-400" /> {rm}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ─── Video Resources Card ─── */}
      <section className="pb-16" data-testid="insights-video-section">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/resources/videos" className="group block">
            <div className="relative rounded-2xl overflow-hidden bg-slate-900 p-8 md:p-10 hover:shadow-xl transition-shadow duration-300">
              <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 80% 50%, #0066CC, transparent 70%)' }} />
              <div className="relative z-10 flex items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center shrink-0">
                    <Film className="w-7 h-7 text-taxera-teal" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-white mb-1 group-hover:text-taxera-teal transition-colors">Video Resources</h3>
                    <p className="text-sm text-slate-400 max-w-lg">Watch product demos, technical deep-dives, and compliance explainers from the Taxera team.</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-taxera-teal group-hover:translate-x-1 transition-all shrink-0" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50" data-testid="insights-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-lg font-bold text-taxera-charcoal mb-4">
            Built for a world that's going digital
          </h2>
          <p className="text-sm text-slate-600 mb-8 max-w-xl mx-auto">
            New mandates don't have to mean new problems. Taxera was built for this.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/products"
              className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-shadow duration-300 hover:scale-105"
              data-testid="insights-products-btn"
            >
              Explore Our Products
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/evaluate"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-taxera-charcoal border border-slate-200 rounded-full font-semibold hover:bg-slate-50 transition-colors duration-300"
              data-testid="insights-evaluate-btn"
            >
              Assess Your Readiness
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default InsightsPage;
